/**
 * Lokalni kalendarski datum YYYY-MM-DD (vremenska zona preglednika, Europe/Zagreb).
 * Zamjena za `toISOString().slice(0, 10)`, koji daje UTC pa je izmedju 00 i 02 h jucerasnji.
 */
export function localIsoDate(d: Date = new Date()): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Iz ISO datuma ili datuma-vremena izvuče YYYY-MM-DD (za daljnji prikaz). */
export function isoDatePart(value: string): string {
    const s = String(value).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) {
        return m[1];
    }
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
        return s.slice(0, 10);
    }

    return s;
}

/**
 * Tekstualni unos datuma (npr. iz HR filtera): `dd.mm.yyyy` ili već `yyyy-mm-dd` → ISO `yyyy-mm-dd`.
 * Nevažeći unos → null.
 */
export function parseDateInputToIso(raw: string): string | null {
    const s = String(raw).trim();
    // Tolerantno prihvaća: dd.mm.yyyy, dd/mm/yyyy, dd-mm-yyyy, dd mm yyyy (s razmacima),
    // ili 8 znamenki bez separatora ddmmyyyy. Sve idu na istu validaciju.
    const dmY = s.match(/^(\d{1,2})[.\-/ ](\d{1,2})[.\-/ ](\d{4})$/) || s.match(/^(\d{2})(\d{2})(\d{4})$/);
    if (dmY) {
        const day = Number(dmY[1]);
        const month = Number(dmY[2]);
        const year = Number(dmY[3]);
        if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
        }
        const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
        if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
            return null;
        }

        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return s;
    }

    return null;
}

/**
 * Datum za prikaz: HR uvijek `dd.mm.yyyy`; EN `dd/mm/yyyy` (en-GB).
 * Prihvaća YYYY-MM-DD ili ISO datetime (samo datumski dio).
 */
export function formatDate(value: string, locale: 'hr' | 'en' = 'hr') {
    if (!value) {
        return '-';
    }
    const datePart = isoDatePart(value);
    const asDate = new Date(`${datePart}T12:00:00`);
    if (Number.isNaN(asDate.getTime())) {
        return String(value);
    }
    if (locale === 'en') {
        return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(asDate);
    }

    const d = String(asDate.getDate()).padStart(2, '0');
    const m = String(asDate.getMonth() + 1).padStart(2, '0');
    const y = asDate.getFullYear();

    return `${d}.${m}.${y}`;
}

export function formatMoney(value: number, locale: 'hr' | 'en' = 'hr') {
    return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'hr-HR', { style: 'currency', currency: 'EUR' }).format(value);
}

export function formatNumber(value: number, locale: 'hr' | 'en' = 'hr') {
    return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'hr-HR').format(value);
}

/**
 * Parsiraj iznos iz korisničkog unosa (tolerantno, hrvatski-svjesno).
 *  Prima: "1.982,11" · "1982,11" · "1982.11" · "1982" · "1 982,11" → number | null.
 *  Pravilo: ako ima zarez, zarez = decimala (točke su tisućice); inače točka = decimala.
 */
export function parseMoneyInput(raw: string): number | null {
    let s = String(raw ?? '').trim().replace(/\s/g, '');
    if (s === '') return null;
    const neg = /^-/.test(s);
    s = s.replace(/[^\d.,]/g, '');
    if (s.includes(',')) {
        s = s.replace(/\./g, '').replace(',', '.');
    }
    const n = Number(s);
    if (Number.isNaN(n)) return null;
    return neg ? -Math.abs(n) : n;
}

/**
 * Formatiraj iznos za polje unosa (bez valute): "1.982,11" (hr) / "1,982.11" (en). 2 decimale.
 */
export function formatMoneyInput(value: number | string | null | undefined, locale: 'hr' | 'en' = 'hr'): string {
    const n = typeof value === 'number' ? value : parseMoneyInput(String(value ?? ''));
    if (n === null || n === undefined || Number.isNaN(n)) return '';
    return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'hr-HR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(n);
}

export function formatMoneyAxis(value: number, locale: 'hr' | 'en' = 'hr') {
    return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'hr-HR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    }).format(value);
}

export function niceAxisTicks(rawMin: number, rawMax: number, targetCount = 4): { min: number; max: number; ticks: number[] } {
    const range = Math.max(rawMax - rawMin, 1);
    const roughStep = range / Math.max(targetCount, 1);
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const norm = roughStep / magnitude;
    const niceStep = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * magnitude;
    const niceMin = Math.floor(rawMin / niceStep) * niceStep;
    const niceMax = Math.ceil(rawMax / niceStep) * niceStep;
    const ticks: number[] = [];
    for (let v = niceMin; v <= niceMax + niceStep / 2; v += niceStep) {
        ticks.push(Number(v.toFixed(10)));
    }

    return { min: niceMin, max: niceMax, ticks };
}

/**
 * Datum i vrijeme: HR uvijek `dd.mm.yyyy` i 24h (`HH:mm` / `HH:mm:ss`).
 * EN koristi en-GB prikaz uklj. 12h gdje Intl tako formatira.
 * Za ISO string koristi literalne komponente iz stringa (bez TZ pomaka).
 */
export function formatDateTime(value: string | Date, locale: 'hr' | 'en' = 'hr') {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            return '-';
        }
        const d = String(value.getDate()).padStart(2, '0');
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const y = value.getFullYear();
        const hh = String(value.getHours()).padStart(2, '0');
        const mm = String(value.getMinutes()).padStart(2, '0');
        const ss = String(value.getSeconds()).padStart(2, '0');
        if (locale === 'en') {
            return new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            }).format(value);
        }

        return `${d}.${m}.${y} ${hh}:${mm}:${ss}`;
    }

    const raw = String(value).trim();
    if (!raw) {
        return '-';
    }

    const iso =
        raw.match(
            /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/,
        ) ?? raw.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (iso) {
        const dd = iso[3];
        const mo = iso[2];
        const yy = iso[1];
        const hh = iso[4];
        const mm = iso[5];
        const ss = iso[6];
        if (locale === 'en') {
            const fake = new Date(`${yy}-${mo}-${dd}T${hh}:${mm}:${ss ?? '00'}`);
            if (!Number.isNaN(fake.getTime())) {
                return new Intl.DateTimeFormat('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: ss ? '2-digit' : undefined,
                    hour12: true,
                }).format(fake);
            }
        }

        return ss ? `${dd}.${mo}.${yy} ${hh}:${mm}:${ss}` : `${dd}.${mo}.${yy} ${hh}:${mm}`;
    }

    const t = Date.parse(raw);
    if (!Number.isNaN(t)) {
        return formatDateTime(new Date(t), locale);
    }

    return raw;
}

/**
 * Broj iz polja za unos: prihvaca decimalni zarez ("12,5") i tocke tisucica
 * ("1.250,5"). Prazno polje je 0 (isto kao prije). Neispravan unos baca gresku,
 * da se ne posalje null i javi "Spremljeno" bez ucinka (audit 40.x, nalaz 17).
 */
export function decimalInput(raw: string | number): number {
    if (typeof raw === 'number') {
        return raw;
    }
    let s = raw.trim().replace(/\s/g, '');
    if (s === '') {
        return 0;
    }
    if (s.includes(',')) {
        s = s.replace(/\./g, '').replace(',', '.');
    }
    const n = Number(s);
    if (!Number.isFinite(n)) {
        throw new Error(`Neispravan broj: "${raw}"`);
    }

    return n;
}
